/**
 * Kontakty CSV export (Phase 11.6.11).
 *
 * Streams the response from `GET /v1/contacts/export/csv` (already shipped
 * pre-11.6.11 — see [api/src/routes/contacts.ts:1135]) and triggers a browser
 * download.
 */

import { API_BASE } from '@/lib/api';
import { getAuthHeaders } from '@/lib/auth';

export interface ContactsCsvFilters {
  /** API numeric type: '1' = rozjednané, '2' = ukončené, '3' = všechny. */
  type: string;
  /** Vehicle brand id, optional. */
  brand?: string;
  /** Seller (uživatel) id, optional. */
  seller?: string;
  /** Last-contact-date lower bound (YYYY-MM-DD), optional. */
  start?: string;
  /** Last-contact-date upper bound (YYYY-MM-DD), optional. */
  end?: string;
}

export async function downloadContactsCsv(filters: ContactsCsvFilters): Promise<void> {
  const params = new URLSearchParams();
  params.set('type', filters.type);
  for (const key of ['brand', 'seller', 'start', 'end'] as const) {
    const value = filters[key];
    if (value != null && value !== '') params.set(key, String(value));
  }

  const headers: Record<string, string> = {
    Accept: 'text/csv',
    ...getAuthHeaders(),
  };

  const res = await fetch(`${API_BASE}/v1/contacts/export/csv?${params.toString()}`, {
    method: 'GET',
    headers,
    credentials: 'same-origin',
  });

  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(text || `HTTP ${res.status}`);
  }

  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const filename = extractFilename(res.headers.get('Content-Disposition')) ?? defaultFilename();

  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

function extractFilename(disposition: string | null): string | null {
  if (!disposition) return null;
  const match = disposition.match(/filename\*?=(?:UTF-8'')?"?([^";]+)"?/i);
  return match?.[1] ?? null;
}

function defaultFilename(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `kontakty_export_${dd}-${mm}-${yyyy}.csv`;
}
